/**
 * AIForumReplyHelper Component - AI assistant for forum replies
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Sparkles, Loader2, Copy, Check, Wand2 } from 'lucide-react';
import { useAIGeneration, getErrorMessage, safeString } from './utils';

export const AIForumReplyHelper = ({ postTitle, postContent, authorName, onInsert }) => {
  const [open, setOpen] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  
  const { loading, generatedText, setGeneratedText, copied, generate, handleCopy } = useAIGeneration('forum-reply');
  
  const safeTitle = safeString(postTitle, 'Untitled');
  const safeContent = safeString(postContent, '');
  const safeAuthor = safeString(authorName, 'Unknown');
  
  const handleGenerate = async () => {
    try {
      const response = await generate({
        post_title: safeTitle,
        post_content: safeContent,
        author_name: safeAuthor,
        additional_context: additionalContext || null
      });
      setGeneratedText(response.generated_text);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate reply'));
    }
  };
  
  const handleInsert = () => {
    if (onInsert) onInsert(generatedText);
    setOpen(false);
    setGeneratedText('');
    setAdditionalContext('');
    toast.success('Reply inserted');
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          AI Suggest Reply
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="w-5 h-5 text-purple-500" />
            AI Reply Suggestion
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <Card className="bg-slate-100">
            <CardContent className="pt-4">
              <p className="text-sm text-gray-600 mb-1">Replying to:</p>
              <p className="font-medium">{safeTitle}</p>
              <p className="text-sm text-gray-500">by {safeAuthor}</p>
            </CardContent>
          </Card>
          
          <div>
            <Label>Additional context (optional)</Label>
            <Textarea
              placeholder="Any specific points you want to address or include..."
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              rows={2}
              className="mt-1"
            />
          </div>
          
          <Button 
            onClick={handleGenerate} 
            disabled={loading}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="w-4 h-4 mr-2" /> Generate Reply</>
            )}
          </Button>
          
          {generatedText && (
            <Card className="bg-slate-50">
              <CardContent className="pt-4">
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm text-gray-600">Suggested Reply:</Label>
                  <Button variant="ghost" size="sm" onClick={() => handleCopy(generatedText)}>
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <div className="whitespace-pre-wrap text-sm bg-white p-3 rounded border max-h-60 overflow-y-auto">
                  {generatedText}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
        
        {generatedText && (
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGeneratedText('')}>
              Regenerate
            </Button>
            <Button onClick={handleInsert} className="bg-cyan-600 hover:bg-cyan-700">
              Use This Reply
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
